import {
  Container,
  Header,
  Input,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TextContent,
  TokenGroup,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_TICKETS } from '../../../services/container-management/queries';
import './style.css';
import { CmsContainer, Ticket } from '../../../services/container-management/types';

export type ContainerTicketsProps = {
  container: CmsContainer;
  containerFullName: string;
};

export default function ContainerTickets(props: ContainerTicketsProps): ReactElement {
  const { t } = useTranslation();
  const { container } = props;
  const { version } = container;
  const { tickets } = container;
  const [ticketString, setTicketString] = useState<string>('');

  const [initialTicketIds] = useState<Ticket[] | undefined>(tickets);
  const [ticketIds, setTicketIds] = useState<{ label: string }[]>([]);

  const [isUpdateTicketsSucceeded, setIsUpdateTicketsSucceeded] = useState(false);
  const [updateTicketsError, setUpdateTicketsError] = useState<string | null>(null);
  const [containerVersion, setContainerVersion] = useState<number>(version);

  const [callUpdateTickets, { loading: updateTicketsLoading }] = useMutation(UPDATE_TICKETS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const tokenTicketIds = result.updateTickets.tickets?.map((ticket: { ticketId: string }) => ({
        label: ticket?.ticketId,
      }));
      if (tokenTicketIds) {
        setTicketIds(tokenTicketIds);
        setIsUpdateTicketsSucceeded(true);
        setContainerVersion(containerVersion + 1);
      }
    },
    onError: (error) => {
      setUpdateTicketsError(error.message);
    },
  });

  useEffect(() => {
    // Populate ticketIds on page load
    if (props.container?.tickets) {
      const tokenTicketIds = initialTicketIds?.map((ticket) => ({
        label: ticket?.ticketId,
        disabled: !container.isActive,
      }));
      if (tokenTicketIds) {
        setTicketIds(tokenTicketIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddedTicket = (addTicket: Ticket): void => {
    setIsUpdateTicketsSucceeded(false);
    setUpdateTicketsError(null);
    callUpdateTickets({
      variables: {
        addedTickets: [addTicket],
        containerId: container.containerId,
        version: containerVersion,
      },
    });
  };

  const handleRemovedTicket = (removedTicket: Ticket): void => {
    setIsUpdateTicketsSucceeded(false);
    setUpdateTicketsError(null);
    callUpdateTickets({
      variables: {
        removedTickets: [removedTicket],
        containerId: container.containerId,
        version: containerVersion,
      },
    });
  };

  const ticketIdStringIsValid = () => ticketString.match('^[A-Za-z0-9]+$');

  return (
    <div>
      <Container
        header={
          <Header data-test-id="header" variant="h2" description="">
            Ticket IDs
          </Header>
        }>
        <SpaceBetween direction="vertical" size="xs">
          <div data-test-id="ticketId-input-div" className="column">
            <div className="flex-align-center">
              <Input
                className="medium_input"
                onChange={({ detail }) => setTicketString(detail.value)}
                value={ticketString}
                // eslint-disable-next-line react/destructuring-assignment
                placeholder={t(`Scan or enter Ticket IDs for ${props.containerFullName}`)}
                type="text"
                disabled={updateTicketsLoading || !container.isActive}
                onKeyDown={({ detail }) => {
                  if (
                    detail.keyCode === 13 &&
                    ticketIdStringIsValid() &&
                    ticketString.length >= 3
                  ) {
                    // update ticketId on enter key press
                    handleAddedTicket({ ticketId: ticketString });
                    setTicketString('');
                  }
                }}
              />
              {isUpdateTicketsSucceeded && (
                <StatusIndicator type="success">Tickets Updated</StatusIndicator>
              )}
              {updateTicketsLoading && (
                <div>
                  <Spinner size="normal" />
                  <p> Updating Tickets </p>
                </div>
              )}
              {updateTicketsError && (
                <StatusIndicator type="error">{updateTicketsError}</StatusIndicator>
              )}
            </div>
            {ticketString.length > 0 && !ticketIdStringIsValid() && (
              <small
                data-test-id="ticketStringError"
                style={{
                  color: 'red',
                }}>
                {t('TicketId provided is invalid')}
              </small>
            )}
            {ticketString.length > 0 && ticketString.length < 3 && (
              <small
                data-test-id="ticketStringError"
                style={{
                  color: 'red',
                }}>
                {t('Enter atleast three characters')}
              </small>
            )}
            <TextContent>
              <p>
                <small> Add up to 10 Ticket IDs. Valid charaters: A-Z,a-z,0-9 </small>
              </p>
            </TextContent>
          </div>
          <div>
            <TokenGroup
              onDismiss={({ detail: { itemIndex } }) => {
                handleRemovedTicket({ ticketId: ticketIds[itemIndex].label });
              }}
              items={ticketIds}
            />
          </div>
        </SpaceBetween>
      </Container>
    </div>
  );
}
